import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Bookmark, MapPin, Home, DollarSign, TrendingUp, AlertTriangle, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function DealDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [deal, setDeal] = useState(null);
  const [isSaved, setIsSaved] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDeal = async () => {
      try {
        const response = await axios.get(`${API}/deals/${id}`);
        setDeal(response.data);
        
        const savedResponse = await axios.get(`${API}/deals/saved/list`);
        setIsSaved(savedResponse.data.includes(id));
      } catch (error) {
        console.error('Error fetching deal:', error);
        toast.error('Failed to load deal details');
      } finally {
        setLoading(false);
      }
    };
    fetchDeal();
  }, [id]);

  const handleToggleSave = async () => {
    try {
      const response = await axios.post(`${API}/deals/save/${id}`);
      setIsSaved(response.data.status === 'saved');
      toast.success(response.data.status === 'saved' ? 'Deal saved!' : 'Deal removed from saved');
    } catch (error) {
      console.error('Error toggling save:', error);
      toast.error('Failed to save deal');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen relative z-10 flex items-center justify-center">
        <p className="font-mono text-zinc-500">LOADING DEAL DATA...</p>
      </div>
    );
  }

  if (!deal) {
    return (
      <div className="min-h-screen relative z-10 flex items-center justify-center">
        <p className="font-mono text-zinc-500">DEAL NOT FOUND</p>
      </div>
    );
  }

  const potentialProfit = deal.estimated_arv - deal.list_price - deal.estimated_repair_cost;
  const roi = ((potentialProfit / (deal.list_price + deal.estimated_repair_cost)) * 100).toFixed(2);
  const costPerSqft = deal.square_feet > 0 ? (deal.list_price / deal.square_feet).toFixed(2) : 0;
  const arvPerSqft = deal.square_feet > 0 ? (deal.estimated_arv / deal.square_feet).toFixed(2) : 0;

  return (
    <div className="min-h-screen relative z-10 text-zinc-100">
      <header className="border-b border-zinc-800 bg-zinc-950">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <Button 
            data-testid="back-to-dashboard-btn"
            onClick={() => navigate('/')} 
            className="bg-transparent border border-zinc-700 hover:border-zinc-500 text-zinc-300 font-mono rounded-none"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <h1 className="text-lg font-mono font-bold uppercase tracking-tight">Deal Analysis</h1>
          <Button 
            data-testid="toggle-save-deal-btn"
            onClick={handleToggleSave} 
            className={`border font-mono rounded-none ${isSaved ? 'bg-green-600 border-green-600 text-black hover:bg-green-500' : 'bg-transparent border-zinc-700 text-zinc-300 hover:border-zinc-500'}`}
          >
            <Bookmark className={`w-4 h-4 mr-2 ${isSaved ? 'fill-current' : ''}`} />
            {isSaved ? 'Saved' : 'Save'}
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div data-testid="deal-image-section" className="space-y-4">
            <img 
              src={deal.image_url} 
              alt={deal.address} 
              className="w-full h-96 object-cover border border-zinc-800"
            />
            
            <div className="bg-zinc-950 border border-zinc-800 p-6">
              <h2 data-testid="deal-address" className="text-2xl font-mono font-bold mb-2">{deal.address}</h2>
              <p data-testid="deal-location" className="text-zinc-400 font-mono mb-4 flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                {deal.city}, {deal.state} {deal.zip_code}
              </p>
              
              <div className="flex flex-wrap gap-2 mb-4">
                {deal.damage_type.map((damage, idx) => (
                  <span key={idx} className="px-3 py-1 bg-red-500/10 border border-red-500/30 text-red-400 text-xs font-mono uppercase">
                    {damage}
                  </span>
                ))}
                {deal.is_off_market && (
                  <span className="px-3 py-1 bg-green-500/10 border border-green-500/30 text-green-400 text-xs font-mono uppercase">
                    Off Market
                  </span>
                )}
              </div>

              <p className="text-zinc-300 text-sm leading-relaxed">{deal.description}</p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="bg-zinc-950 border border-zinc-800 p-6">
              <h3 className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-4">Property Details</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-mono uppercase text-zinc-500">Property Type</p>
                  <p className="text-lg font-mono font-bold">{deal.property_type}</p>
                </div>
                <div>
                  <p className="text-xs font-mono uppercase text-zinc-500">Condition</p>
                  <p className="text-lg font-mono font-bold text-red-500">{deal.condition}</p>
                </div>
                <div>
                  <p className="text-xs font-mono uppercase text-zinc-500">Bedrooms</p>
                  <p className="text-lg font-mono font-bold">{deal.bedrooms}</p>
                </div>
                <div>
                  <p className="text-xs font-mono uppercase text-zinc-500">Bathrooms</p>
                  <p className="text-lg font-mono font-bold">{deal.bathrooms}</p>
                </div>
                <div>
                  <p className="text-xs font-mono uppercase text-zinc-500">Square Feet</p>
                  <p className="text-lg font-mono font-bold">{deal.square_feet.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-xs font-mono uppercase text-zinc-500">Year Built</p>
                  <p className="text-lg font-mono font-bold">{deal.year_built}</p>
                </div>
                <div>
                  <p className="text-xs font-mono uppercase text-zinc-500">Lot Size</p>
                  <p className="text-lg font-mono font-bold">{deal.lot_size} acres</p>
                </div>
                <div>
                  <p className="text-xs font-mono uppercase text-zinc-500">Days on Market</p>
                  <p className="text-lg font-mono font-bold">{deal.days_on_market}</p>
                </div>
              </div>
            </div>

            <div className="bg-zinc-950 border border-green-500/50 p-6">
              <h3 className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-4">Financial Analysis</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center pb-2 border-b border-zinc-800">
                  <span className="text-sm font-mono text-zinc-400">List Price</span>
                  <span className="text-xl font-mono font-bold">${deal.list_price.toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center pb-2 border-b border-zinc-800">
                  <span className="text-sm font-mono text-zinc-400">Cost per Sq Ft</span>
                  <span className="text-xl font-mono font-bold text-yellow-500">${costPerSqft}/sqft</span>
                </div>
                <div className="flex justify-between items-center pb-2 border-b border-zinc-800">
                  <span className="text-sm font-mono text-zinc-400">Repair Cost</span>
                  <span className="text-xl font-mono font-bold text-red-500">-${deal.estimated_repair_cost.toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center pb-2 border-b border-zinc-800">
                  <span className="text-sm font-mono text-zinc-400">After Repair Value</span>
                  <span className="text-xl font-mono font-bold text-green-500">${deal.estimated_arv.toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center pb-2 border-b border-zinc-800">
                  <span className="text-sm font-mono text-zinc-400">ARV per Sq Ft</span>
                  <span className="text-xl font-mono font-bold text-green-400">${arvPerSqft}/sqft</span>
                </div>
                <div className="flex justify-between items-center pt-2">
                  <span className="text-sm font-mono text-zinc-400">Potential Profit</span>
                  <span className="text-2xl font-mono font-bold text-green-400">${potentialProfit.toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center pt-2 bg-green-500/10 -mx-6 px-6 py-3">
                  <span className="text-sm font-mono uppercase text-green-400">ROI</span>
                  <span className="text-3xl font-mono font-bold text-green-400">{roi}%</span>
                </div>
              </div>
            </div>

            <Button 
              data-testid="run-underwriter-analysis-btn"
              onClick={() => navigate('/underwriter')} 
              className="w-full bg-green-600 hover:bg-green-500 text-black font-mono uppercase tracking-wider rounded-none py-6 text-lg"
            >
              Run Full Underwriter Analysis
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}